
import { defineComponent } from 'vue';
import MainLayout from '@/layout/MainLayout.vue';
import { useAuthStore } from '@/store/AuthStore';
// import { useArtworksStore } from "@/store/ArtworksStore";
import { useGalleryStore } from '@/store/GalleriesStore';
import { useArtistsStore } from '@/store/ArtistsStore';
import { usePrivateSellersStore } from '@/store/PrivateSellersStore';
import { ArtworksRecord } from '@/dataTypes/types';
import ArtworkCard from '@/components/artworks/ArtworkCard.vue';
import InsideSearch from '@/components/search/InsideSearch.vue';
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue';
import PaginationComponent from '@/components/pagination/PaginationComponent.vue';
import ArtworkImagePopup from '@/components/artworks/ArtworkImagePopup.vue';
import { Container, Draggable } from 'vue3-smooth-dnd';
import _, { DebouncedFunc } from 'lodash';

export default defineComponent({
  components: {
    MainLayout,
    InsideSearch,
    ArtworkCard,
    SpinnerLoader,
    PaginationComponent,
    ArtworkImagePopup,
    Container,
    Draggable,
  },
  data() {
    const authStore = useAuthStore();
    // const artworksStore = useArtworksStore();
    const artistsStore = useArtistsStore();
    const galleryStore = useGalleryStore();
    const privateSeller = usePrivateSellersStore();
    return {
      // artworksStore,
      authStore,
      artistsStore,
      galleryStore,
      privateSeller,
      role: '',
      artworks: [] as ArtworksRecord[],
      artworksLength: [] as ArtworksRecord[],
      allArtistArtworks: [] as ArtworksRecord[],
      artistArtworks: [] as ArtworksRecord[],
      artistArtworksLength: 0,
      search: '',
      isLoading: true,
      offset: 0,
      limit: 20,
      popupId: -1,
      isOpen: false,
      currentPage: 1,
      isSaving: false,
      debouncedSearch: null as DebouncedFunc<(keyword: string) => void> | null,
    };
  },
  async created() {
    if (this.authStore.isLoggedIn) {
      this.role = this.authStore.userRole;
      if (this.isGalleryOrPrivateSeller) {
        await this.getMyArtworks();
      } else {
        await this.getArtistArtworks();
      }
      this.isLoading = false;
      this.debouncedSearch = _.debounce(async (keyword: string) => {
        await this.searchArtworks(keyword);
      }, 500);
    }
  },
  methods: {
    async getMyArtworks(keyword?: string) {
      this.artworksLength = await (
        this.currentStore as any
      ).getMyArtworksLength(keyword);
      this.artworks = this.artworksLength.slice(this.offset, this.limit);
    },
    async getArtistArtworks(keyword?: string) {
      const { items: artistArtworks, total: artistArtworksLength } =
        await this.artistsStore.fetchArtistArtworks(keyword);
      this.allArtistArtworks = artistArtworks;
      this.artistArtworks = this.allArtistArtworks.slice(
        this.offset,
        this.limit
      );
      this.artistArtworksLength = artistArtworksLength;
    },
    createIdOrderMap(artworks: any[]) {
      const idOrderMap: any = {};
      const artistArtworks = artworks.reverse();
      artistArtworks.forEach((item, index) => {
        const { id } = item;
        idOrderMap[id] = index + 1;
      });
      return idOrderMap;
    },
    async saveOrder() {
      if (this.isGalleryOrPrivateSeller) {
        const sortOrder = this.createIdOrderMap(this.artworksLength);
        this.isSaving = true;
        await this.currentStore.saveOrderOfArtwork(sortOrder);
        this.isSaving = false;
      } else {
        const sortOrder = this.createIdOrderMap(this.allArtistArtworks);
        this.isSaving = true;
        await this.artistsStore.saveOrderOfArtwork(sortOrder);
        this.isSaving = false;
      }
    },
    filterArtworks(search: string) {
      this.search = search;
    },
    async updatePagination(pageNumber: number) {
      this.isLoading = true;
      this.offset = (pageNumber - 1) * this.limit;
      if (this.isGalleryOrPrivateSeller) {
        this.artworks = this.artworksLength.slice(
          this.offset,
          this.offset + this.limit
        );
      } else if (this.role === 'artist') {
        this.artistArtworks = this.allArtistArtworks.slice(
          this.offset,
          this.offset + this.limit
        );
      }
      this.currentPage = pageNumber;
      this.isLoading = false;
    },
    duplicateArtwork(id: number) {
      this.isLoading = true;
      this.$router.push(`/artworks/${id}/duplicate`);
    },
    displayArtworkImages(id: number) {
      this.isOpen = true;
      this.popupId = id;
    },
    getArtworkId(): ArtworksRecord | undefined {
      return this.artworks.find(
        (artwork: ArtworksRecord) => artwork.id === this.popupId
      );
    },
    async onDrop(dropResult: any) {
      let { removedIndex, addedIndex } = dropResult;
      if (this.currentPage > 1) {
        removedIndex = removedIndex + this.offset;
        addedIndex = addedIndex + this.offset;
      }
      if (
        removedIndex !== null &&
        addedIndex !== null &&
        this.isGalleryOrPrivateSeller
      ) {
        const movedItem = this.artworksLength.splice(removedIndex, 1)[0];
        this.artworksLength.splice(addedIndex, 0, movedItem);
        if (this.role === 'gallery') {
          this.galleryStore.galleryArtworksLength = this.artworksLength;
        }
        const artworkIds = this.artworksLength.map((item) => item.id);
        this.artworks = this.artworksLength.slice(
          this.offset,
          this.offset + this.limit
        );
      } else if (
        removedIndex !== null &&
        addedIndex !== null &&
        this.role === 'artist'
      ) {
        const movedItem = this.allArtistArtworks.splice(removedIndex, 1)[0];
        this.allArtistArtworks.splice(addedIndex, 0, movedItem);
        this.artistsStore.artistArtworks = this.allArtistArtworks;
        const artworkIds = this.allArtistArtworks.map((item) => item.id);
        this.artistArtworks = this.allArtistArtworks.slice(
          this.offset,
          this.offset + this.limit
        );
      }
    },
    async moveUp(id: number) {
      const index = this.artworksLength.findIndex((item) => item.id === id);
      const artistIndex = this.allArtistArtworks.findIndex(
        (item) => item.id === id
      );

      if (index !== -1 && this.isGalleryOrPrivateSeller) {
        if (index === 0 && this.offset === 0) {
          return;
        }
        const movedItem = this.artworksLength.splice(index, 1)[0];
        this.artworksLength.splice(index - 1, 0, movedItem);
        // this.artistsStore.allGalleryArtists = this.artistsLength;
        const artworkIds = this.artworksLength.map((item) => item.id);
        this.artworks = this.artworksLength.slice(
          this.offset,
          this.offset + this.limit
        );
      }
      if (artistIndex !== -1 && this.role === 'artist') {
        if (index === 0 && this.offset === 0) {
          return;
        }
        const movedItem = this.allArtistArtworks.splice(artistIndex, 1)[0];
        this.allArtistArtworks.splice(artistIndex - 1, 0, movedItem);
        // this.artistsStore.allGalleryArtists = this.artistsLength;
        const artworkIds = this.allArtistArtworks.map((item) => item.id);
        this.artistArtworks = this.allArtistArtworks.slice(
          this.offset,
          this.offset + this.limit
        );
      }
    },
    async moveDown(id: number) {
      const index = this.artworksLength.findIndex((item) => item.id === id);
      const artistIndex = this.allArtistArtworks.findIndex(
        (item) => item.id === id
      );
      if (index !== -1 && this.isGalleryOrPrivateSeller) {
        const movedItem = this.artworksLength.splice(index, 1)[0];
        this.artworksLength.splice(index + 1, 0, movedItem);
        // this.artistsStore.allGalleryArtists = this.artistsLength;
        const artistsIds = this.artworksLength.map((item) => item.id);
        this.artworks = this.artworksLength.slice(
          this.offset,
          this.offset + this.limit
        );
      }
      if (artistIndex !== -1 && this.role === 'artist') {
        const movedItem = this.allArtistArtworks.splice(artistIndex, 1)[0];
        this.allArtistArtworks.splice(artistIndex + 1, 0, movedItem);
        // this.artistsStore.allGalleryArtists = this.artistsLength;
        const artistsIds = this.allArtistArtworks.map((item) => item.id);
        this.artistArtworks = this.allArtistArtworks.slice(
          this.offset,
          this.offset + this.limit
        );
      }
    },
    async searchArtworks(keyword: string) {
      if (this.isGalleryOrPrivateSeller) {
        await this.getMyArtworks(keyword);
      } else if (this.role === 'artist') {
        await this.getArtistArtworks(keyword);
      }
    },
  },
  computed: {
    isPrivateSeller() {
      return this.authStore.userRole === 'private_seller';
    },
    currentStore() {
      return this.isPrivateSeller ? this.privateSeller : this.galleryStore;
    },
    isGalleryOrPrivateSeller() {
      return this.role === 'gallery' || this.role === 'private_seller';
    },
    filteredArtworks(): ArtworksRecord[] {
      if (this.isGalleryOrPrivateSeller) {
        return this.artworks;
      } else if (this.role === 'artist') {
        return this.artistArtworks;
      }
      return [];
    },
    length(): number {
      return this.artworksLength.length;
    },
    pageSize(): number {
      if (this.isGalleryOrPrivateSeller) {
        if (this.artworksLength.length < 20) {
          return this.artworksLength.length;
        } else {
          return 20;
        }
      } else {
        if (this.allArtistArtworks.length < 20) {
          return this.allArtistArtworks.length;
        } else {
          return 20;
        }
      }
    },
  },
  watch: {
    async search(newValue: string, oldValue: string) {
      if (this.debouncedSearch) {
        this.debouncedSearch(newValue);
      }
    },
  },
});
